import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const OTP_INPUT_SX = {
  width: 50,
  height: 62,
  textAlign: "center" as const,
  fontSize: "1.75rem",
  fontFamily:
    "'SF Mono', 'Fira Code', 'Fira Mono', 'Consolas', 'Courier New', monospace",
  fontWeight: 500,
  border: "2px solid",
  borderColor: "divider",
  borderRadius: "8px",
  outline: "none",
  background: "transparent",
  color: "text.primary",
  cursor: "text",
  transition: "border-color 0.15s ease",
  WebkitAppearance: "none",
  MozAppearance: "textfield",
  "&:focus": { borderColor: "primary.main" },
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
  },
} as const;

interface TotpInputProps {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export default function TotpInput({
  value,
  onChange,
  autoFocus,
  disabled,
}: TotpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
  }, [autoFocus]);

  const getDigit = (i: number) => value[i] ?? "";

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const chars = value.padEnd(6, " ").split("");
    chars[index] = digit || " ";
    onChange(chars.join("").replace(/\s+$/, ""));
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!getDigit(index) && index > 0) {
        const chars = value.padEnd(6, " ").split("");
        chars[index - 1] = " ";
        onChange(chars.join("").replace(/\s+$/, ""));
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        my: 2,
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            inputRefs.current[i] = el;
          }}
          value={getDigit(i)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(i, e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(i, e)
          }
          onPaste={handlePaste}
          maxLength={2}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={disabled}
          sx={OTP_INPUT_SX}
        />
      ))}
      <Typography
        sx={{
          fontSize: "1.5rem",
          color: "text.disabled",
          lineHeight: 1,
          px: 0.25,
          userSelect: "none",
        }}
      >
        –
      </Typography>
      {[3, 4, 5].map((i) => (
        <Box
          key={i}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            inputRefs.current[i] = el;
          }}
          value={getDigit(i)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(i, e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(i, e)
          }
          onPaste={handlePaste}
          maxLength={2}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={disabled}
          sx={OTP_INPUT_SX}
        />
      ))}
    </Box>
  );
}
